import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createBlock as _createBlock, unref as _unref } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "section-rule"
};
const _hoisted_2 = {
    key: 0,
    class: "crit-actions"
};
const _hoisted_3 = {
    key: 0,
    class: "page-chips-container"
};
const _hoisted_4 = {
    key: 0,
    class: "crit-children"
};
import { computed, inject } from "vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'PolicyReviewCriteriaItem',
    props: {
        criterion: {},
        currentPageId: { default: null },
        pageManager: { default: null },
        status: { default: null },
        attachedPages: { default: null }
    },
    emits: ["status-change", "page-action", "highlight-evidence"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // Inject helper functions from parent
        const getCriterionStatus = inject("getCriterionStatus", (criterion) => criterion.status || "pending");
        const getPageAttachments = inject("getPageAttachments", () => null);
        const hasChildren = computed(() => {
            return props.criterion.children && props.criterion.children.length > 0;
        });
        const isSection = computed(() => props.criterion.type === "section");
        const currentStatus = computed(() => {
            return props.status || props.criterion.status || "pending";
        });
        const statusIcon = computed(() => getStatusIcon(currentStatus.value));
        const statusColor = computed(() => getStatusColor(currentStatus.value));
        const statusLabel = computed(() => {
            if (currentStatus.value === "pass")
                return "Present";
            if (currentStatus.value === "fail")
                return "Missing";
            return "Pending";
        });
        // Stock group descriptions restated tersely.
        const STOCK_DESCRIPTIONS = [
            [/^one or more of the following criteria must be met\.?$/i, "One of the following"],
            [/^all of the following criteria must be met\.?$/i, "All of the following"],
            [/^(\w+) or more of the following criteria must be met\.?$/i, "At least $1 of the following"],
        ];
        const displayText = computed(() => {
            const text = props.criterion.text || "";
            if (!hasChildren.value || isSection.value)
                return text;
            for (const [pattern, replacement] of STOCK_DESCRIPTIONS) {
                if (pattern.test(text)) {
                    const restated = text.replace(pattern, replacement).toLowerCase();
                    return restated.charAt(0).toUpperCase() + restated.slice(1);
                }
            }
            return text;
        });
        const lastMatchingChildIndex = computed(() => {
            const children = props.criterion.children || [];
            let last = -1;
            children.forEach((child, index) => {
                if (getCriterionStatus(child) === currentStatus.value)
                    last = index;
            });
            return last;
        });
        const sectionRule = computed(() => {
            if (!isSection.value || !hasChildren.value)
                return null;
            if (props.criterion.operator === "AT_LEAST")
                return `at least ${props.criterion.threshold ?? 1} of the following`;
            if (props.criterion.logic === "ALL")
                return "all of the following";
            return "one of the following";
        });
        const statusOptions = [
            { value: "pass", label: "Present" },
            { value: "fail", label: "Missing" },
            { value: "pending", label: "Pending" },
        ];
        const getStatusIcon = (status) => {
            if (status === "pass")
                return "mdi-check-circle";
            if (status === "fail")
                return "mdi-close-circle";
            return "mdi-alert-circle";
        };
        const getStatusColor = (status) => {
            if (status === "pass")
                return "success";
            if (status === "fail")
                return "error";
            return "warning";
        };
        // Pages shown for this criterion, by precedence (no duplicate chips across tiers):
        //   1. manual override (page_attachments entry) — authoritative once it exists, even when empty
        //      (user removed every page); navigate-only, no box.
        //   2. resolved pageEvidence — durable page identity + box; drives the highlight.
        //   3. legacy pageNumbers / pageRef — old orders with no resolved evidence; navigate-only.
        const allPageChips = computed(() => {
            if (props.attachedPages !== null) {
                return props.attachedPages.map((pageId) => ({
                    id: pageId,
                    displayNumber: (props.pageManager?.getPageNumber(pageId) ?? 0) + 1,
                }));
            }
            if (props.criterion.pageEvidence && props.criterion.pageEvidence.length > 0) {
                // Skip evidence whose page isn't in the viewer's page set (getPageNumber === -1) — an
                // unresolvable chip would render "Page 0" and navigate nowhere.
                return props.criterion.pageEvidence
                    .filter((evidence) => (props.pageManager?.getPageNumber(evidence.pageId) ?? -1) !== -1)
                    .map((evidence) => ({
                    id: evidence.pageId,
                    displayNumber: (props.pageManager?.getPageNumber(evidence.pageId) ?? 0) + 1,
                    boundingBox: evidence.boundingBox,
                }));
            }
            const chips = [];
            if (props.criterion.pageNumbers && props.criterion.pageNumbers.length > 0) {
                props.criterion.pageNumbers.forEach((pageNum) => {
                    const pageId = props.pageManager?.pages[pageNum]?.id;
                    if (pageId) {
                        chips.push({ id: pageId, displayNumber: pageNum + 1 });
                    }
                });
            }
            else if (props.criterion.pageRef !== undefined) {
                const pageId = props.pageManager?.pages[props.criterion.pageRef]?.id;
                if (pageId) {
                    chips.push({ id: pageId, displayNumber: props.criterion.pageRef + 1 });
                }
            }
            return chips;
        });
        const onStatusSelect = (newStatus) => {
            emit("status-change", props.criterion.id, newStatus);
        };
        const onAddPage = () => {
            if (props.currentPageId) {
                emit("page-action", props.criterion.id, "add", props.currentPageId);
            }
        };
        const onRemovePage = (pageId) => {
            emit("page-action", props.criterion.id, "remove", pageId);
        };
        // Event handlers for child component events
        const handleStatusChange = (criterionId, newStatus) => {
            emit("status-change", criterionId, newStatus);
        };
        const handlePageAction = (criterionId, action, pageId) => {
            emit("page-action", criterionId, action, pageId);
        };
        const handleHighlightEvidence = (payload) => {
            emit("highlight-evidence", payload);
        };
        // A chip with a box drives navigate+highlight through the emit (the page-preview path handles
        // navigation), so don't also goToPage here — that would double-navigate. Box-less chips navigate only.
        const onChipClick = (pageChip) => {
            if (!props.pageManager)
                return;
            if (pageChip.boundingBox) {
                emit("highlight-evidence", {
                    pageId: pageChip.id,
                    boundingBox: pageChip.boundingBox,
                    fieldKey: props.criterion.text ?? "",
                });
                return;
            }
            const pageIndex = props.pageManager.getPageNumber(pageChip.id);
            if (pageIndex !== -1) {
                props.pageManager.goToPage(pageIndex);
            }
        };
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_list_item_title = _resolveComponent("v-list-item-title");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_tooltip = _resolveComponent("v-tooltip");
            const _component_v_chip = _resolveComponent("v-chip");
            const _component_PolicyReviewCriteriaItem = _resolveComponent("PolicyReviewCriteriaItem", true);
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["criteria-item", [{ 'criteria-item--section': isSection.value }, hasChildren.value ? `criteria-item--${currentStatus.value}` : '']])
            }, [
                _createElementVNode("div", {
                    class: _normalizeClass(["crit-row", { 'crit-row--group': hasChildren.value }])
                }, [
                    _createVNode(_component_v_menu, { location: "bottom start" }, {
                        activator: _withCtx(({ props: menuProps }) => [
                            _createVNode(_component_v_btn, _mergeProps(menuProps, {
                                size: "small",
                                variant: "text",
                                color: statusColor.value,
                                class: "status-selector",
                                "aria-label": `Status: ${statusLabel.value}. Click to override.`,
                                icon: ""
                            }), {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                        icon: statusIcon.value,
                                        size: isSection.value ? 26 : 22
                                    }, null, 8, ["icon", "size"])
                                ]),
                                _: 1
                            }, 16, ["color", "aria-label"])
                        ]),
                        default: _withCtx(() => [
                            _createVNode(_component_v_list, { density: "compact" }, {
                                default: _withCtx(() => [
                                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusOptions, (statusOption) => {
                                        return _createVNode(_component_v_list_item, {
                                            key: statusOption.value,
                                            onClick: ($event) => (onStatusSelect(statusOption.value))
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_list_item_title, { class: "d-flex align-center gap-2" }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_v_icon, {
                                                            icon: getStatusIcon(statusOption.value),
                                                            color: getStatusColor(statusOption.value),
                                                            size: "small"
                                                        }, null, 8, ["icon", "color"]),
                                                        _createTextVNode(" " + _toDisplayString(statusOption.label), 1)
                                                    ]),
                                                    _: 2
                                                }, 1024)
                                            ]),
                                            _: 2
                                        }, 1032, ["onClick"]);
                                    }), 64))
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    }),
                    _createElementVNode("span", {
                        class: _normalizeClass(["crit-text", { 'crit-text--group': hasChildren.value, 'crit-text--section': isSection.value }])
                    }, [
                        _createTextVNode(_toDisplayString(displayText.value) + " ", 1),
                        (isSection.value && sectionRule.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_1, "· " + _toDisplayString(sectionRule.value), 1))
                            : _createCommentVNode("", true)
                    ], 2),
                    (allPageChips.value.length > 0 || __props.currentPageId)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            (allPageChips.value.length > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allPageChips.value, (pageChip) => {
                                        return (_openBlock(), _createBlock(_component_v_chip, {
                                            key: pageChip.id,
                                            size: "x-small",
                                            density: "compact",
                                            color: pageChip.boundingBox ? 'blue-gray' : 'grey',
                                            variant: pageChip.boundingBox ? 'tonal' : 'outlined',
                                            class: "cursor-pointer page-chip",
                                            onClick: ($event) => (onChipClick(pageChip)),
                                            "onClick:close": ($event) => (onRemovePage(pageChip.id))
                                        }, {
                                            prepend: _withCtx(() => [
                                                _createVNode(_component_v_icon, {
                                                    icon: pageChip.boundingBox ? 'mdi-image-filter-center-focus' : 'mdi-map-marker-off-outline',
                                                    size: "12",
                                                    class: "mr-1"
                                                }, null, 8, ["icon"])
                                            ]),
                                            close: _withCtx(() => [
                                                _createVNode(_component_v_icon, {
                                                    icon: "mdi-close",
                                                    size: "12"
                                                })
                                            ]),
                                            default: _withCtx(() => [
                                                _createTextVNode(" Page " + _toDisplayString(pageChip.displayNumber) + " ", 1),
                                                _createVNode(_component_v_tooltip, {
                                                    activator: "parent",
                                                    location: "top",
                                                    "open-delay": "300"
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(pageChip.boundingBox
                                                            ? "Highlights the exact evidence on this page."
                                                            : "No exact highlight region could be pinpointed on this page."), 1)
                                                    ]),
                                                    _: 2
                                                }, 1024)
                                            ]),
                                            _: 2
                                        }, 1032, ["color", "variant", "onClick", "onClick:close"]));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true),
                            (__props.currentPageId)
                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                    key: 1,
                                    size: "x-small",
                                    color: "primary",
                                    variant: "text",
                                    class: "assign-page-btn",
                                    onClick: onAddPage
                                }, {
                                    default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                                            _createTextVNode(" Assign Page ", -1)
                                        ]))]),
                                    _: 1
                                }))
                                : _createCommentVNode("", true)
                        ]))
                        : _createCommentVNode("", true)
                ], 2),
                (hasChildren.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.criterion.children, (child, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: child.id,
                                class: _normalizeClass(["crit-branch", [`crit-branch--${_unref(getCriterionStatus)(child)}`, { 'crit-branch--tail': index > lastMatchingChildIndex.value }]])
                            }, [
                                _createVNode(_component_PolicyReviewCriteriaItem, {
                                    criterion: child,
                                    "current-page-id": __props.currentPageId,
                                    "page-manager": __props.pageManager,
                                    status: _unref(getCriterionStatus)(child),
                                    "attached-pages": _unref(getPageAttachments)(child.id),
                                    onStatusChange: handleStatusChange,
                                    onPageAction: handlePageAction,
                                    onHighlightEvidence: handleHighlightEvidence
                                }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"])
                            ], 2));
                        }), 128))
                    ]))
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
